import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../design-system';
import '../styles/ComplianceMiniCalendar.css';

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const dateKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// A month calendar that marks days with statutory due dates, plus a detail list
// that shows either a picked day's items or the next things due.
function ComplianceMiniCalendar({ instances, isOverdue }) {
  const navigate = useNavigate();
  const [viewDate, setViewDate] = useState(() => startOfDay(new Date()));
  const [selectedKey, setSelectedKey] = useState(null); // clicked day, or null
  const today = useMemo(() => startOfDay(new Date()), []);

  const dueByDay = useMemo(() => {
    const map = new Map();
    instances.forEach((i) => {
      if (!i.statutoryDueDate) return;
      const key = dateKey(startOfDay(new Date(i.statutoryDueDate)));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    return map;
  }, [instances]);

  const gridDays = useMemo(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const leadingOffset = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-start
    const start = new Date(y, m, 1 - leadingOffset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [viewDate]);

  // Open, sorted by due date. Overdue first (they're the past-dated ones).
  const openSorted = useMemo(
    () =>
      instances
        .filter((i) => i.statutoryDueDate && !['filed', 'late_filed', 'not_applicable'].includes(i.status))
        .sort((a, b) => new Date(a.statutoryDueDate) - new Date(b.statutoryDueDate)),
    [instances]
  );

  const daysUntil = (d) => Math.round((startOfDay(d) - today) / 86400000);

  // The list shows the picked day's items, else the next open items.
  const selectedItems = selectedKey ? (dueByDay.get(selectedKey) || []) : null;
  const listItems = selectedItems ?? openSorted.slice(0, 6);
  const moreCount = selectedItems ? 0 : Math.max(0, openSorted.length - 6);
  const overdueCount = openSorted.filter(isOverdue).length;

  const goToClient = (i) => { if (i.clientId?._id) navigate(`/org-admin/clients/${i.clientId._id}`); };

  const statusText = (i) => {
    if (isOverdue(i)) return { label: 'Overdue', cls: 'danger' };
    const delta = daysUntil(new Date(i.statutoryDueDate));
    if (delta === 0) return { label: 'Today', cls: 'warn' };
    if (delta < 0) return { label: `${-delta}d ago`, cls: 'muted' };
    return { label: `in ${delta}d`, cls: delta <= 7 ? 'warn' : 'muted' };
  };

  // selectedKey is `${year}-${monthIndex}-${date}` (month is 0-indexed).
  const selectedLabel = useMemo(() => {
    if (!selectedKey) return '';
    const [y, m, d] = selectedKey.split('-').map(Number);
    return new Date(y, m, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  }, [selectedKey]);

  return (
    <Card className="cc-cal" padding="md">
      <div className="cc-cal-head">
        <button type="button" className="cc-cal-nav" onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))} aria-label="Previous month"><ChevronLeft /></button>
        <div className="cc-cal-title">
          <span>{MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
          <button type="button" className="cc-cal-today" onClick={() => { setViewDate(startOfDay(new Date())); setSelectedKey(null); }}>Today</button>
        </div>
        <button type="button" className="cc-cal-nav" onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))} aria-label="Next month"><ChevronRight /></button>
      </div>

      <div className="cc-cal-weekdays">
        {WEEKDAYS.map((w, i) => <span key={i}>{w}</span>)}
      </div>

      <div className="cc-cal-grid">
        {gridDays.map((day) => {
          const key = dateKey(day);
          const inMonth = day.getMonth() === viewDate.getMonth();
          const isToday = key === dateKey(today);
          const dues = dueByDay.get(key) || [];
          const hasOverdue = dues.some(isOverdue);
          const cls = [
            'cc-cal-day',
            !inMonth && 'muted',
            isToday && 'today',
            selectedKey === key && 'selected',
            dues.length > 0 && (hasOverdue ? 'has-overdue' : 'has-due'),
          ].filter(Boolean).join(' ');
          return (
            <button
              key={day.toISOString()}
              type="button"
              className={cls}
              disabled={dues.length === 0}
              onClick={() => setSelectedKey((k) => (k === key ? null : key))}
              title={dues.length ? `${dues.length} due` : undefined}
            >
              <span className="cc-cal-daynum">{day.getDate()}</span>
              {dues.length > 0 && <span className={`cc-cal-dot ${hasOverdue ? 'danger' : 'due'}`} />}
            </button>
          );
        })}
      </div>

      <div className="cc-cal-legend">
        <span><i className="cc-dot due" />Due</span>
        <span><i className="cc-dot danger" />Overdue</span>
        <span><i className="cc-cal-legend-today" />Today</span>
      </div>

      <div className="cc-cal-list">
        <div className="cc-cal-list-head">
          {selectedKey ? (
            <>
              <span className="cc-cal-list-title">Due on {selectedLabel}</span>
              <button type="button" className="cc-cal-list-clear" onClick={() => setSelectedKey(null)}>Show upcoming</button>
            </>
          ) : (
            <>
              <span className="cc-cal-list-title">Upcoming</span>
              {overdueCount > 0 && <span className="cc-cal-overdue-chip">{overdueCount} overdue</span>}
            </>
          )}
        </div>

        {listItems.length === 0 ? (
          <div className="cc-cal-empty">{selectedKey ? 'Nothing due this day.' : 'Nothing due — all caught up.'}</div>
        ) : (
          listItems.map((i) => {
            const s = statusText(i);
            const due = new Date(i.statutoryDueDate);
            return (
              <button key={i._id} type="button" className="cc-cal-item" onClick={() => goToClient(i)} title={i.clientId?.displayName ? `Open ${i.clientId.displayName}` : undefined}>
                <span className={`cc-cal-item-bar ${s.cls === 'danger' ? 'danger' : s.cls === 'warn' ? 'warn' : 'due'}`} />
                <span className="cc-cal-item-meta">
                  <span className="cc-cal-item-name">{i.clientId?.displayName || 'Client'}</span>
                  <span className="cc-cal-item-sub">{i.enrollmentId?.complianceTypeId?.name || 'Compliance'} · {i.periodLabel}</span>
                </span>
                <span className="cc-cal-item-when">
                  <span>{due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                  <span className={`cc-cal-item-status ${s.cls}`}>{s.label}</span>
                </span>
              </button>
            );
          })
        )}

        {moreCount > 0 && <div className="cc-cal-more">+{moreCount} more open</div>}
      </div>
    </Card>
  );
}

export default ComplianceMiniCalendar;
